/**
 * Supported storage providers
 */
export enum StorageProvider {
  GOOGLE_CLOUD = "gcs",
  LOCAL = "local",
  // Future providers
  // S3 = 's3',
  // VERCEL = 'vercel',
}

/**
 * File naming strategies
 */
export enum FileNamingStrategy {
  UUID = "uuid",
  TIMESTAMP = "timestamp",
  ORIGINAL = "original",
  ORIGINAL_UUID = "original_uuid",
  UUID_AND_TIMESTAMP = "uuid_and_timestamp",
}

/**
 * Access mode for uploaded files
 */
export enum FileAccessMode {
  PUBLIC = "public", // Anyone can access
}

/**
 * Directory structure templates
 */
export enum DirectoryTemplate {
  WHITELABEL_EVENT = "whitelabels/{whitelabelId}/events/{eventId}", // Removed trailing slash
  WHITELABEL_ONLY = "whitelabels/{whitelabelId}", // Removed trailing slash
  WHITELABEL_LOGO = "whitelabels/{whitelabelId}/logo", // Removed trailing slash
  WHITELABEL_FAVICON = "whitelabels/{whitelabelId}/favicon", // Removed trailing slash
  WHITELABEL_THUMBNAIL = "whitelabels/{whitelabelId}/thumbnail", // Removed trailing slash
  USER_CONTENT = "users/{userId}", // Removed trailing slash
  USER_IMAGE = "users/{userId}/images", // Removed trailing slash
  TEMP = "temp/{uuid}", // Removed trailing slash
  WALLET_PASSES = "whitelabels/{whitelabelId}/events/{eventId}/wallet-passes", // Removed trailing slash
  PROFILE = "whitelabels/{whitelabelId}/events/{eventId}/profile", // Removed trailing slash
  GENERATED_FILES = "whitelabels/{whitelabelId}/events/{eventId}/generated/[type]", // Removed trailing slash
  TICKETS = "whitelabels/{whitelabelId}/events/{eventId}/tickets", // Removed trailing slash
  BADGES = "whitelabels/{whitelabelId}/events/{eventId}/badges", // Removed trailing slash
  ATTACHMENTS = "whitelabels/{whitelabelId}/events/{eventId}/attachments", // Removed trailing slash
  FLOOR_PLAN = "whitelabels/{whitelabelId}/events/{eventId}/floor-plan", // Removed trailing slash
  IMPORTS = "whitelabels/{whitelabelId}/events/{eventId}/imports", // Removed trailing slash
  IMAGES = "whitelabels/{whitelabelId}/events/{eventId}/images/[type]", // Removed trailing slash
  BANNERS = "whitelabels/{whitelabelId}/events/{eventId}/images/banners", // Removed trailing slash
  LOGOS = "whitelabels/{whitelabelId}/events/{eventId}/images/logos", // Removed trailing slash
  FAVICONS = "whitelabels/{whitelabelId}/events/{eventId}/images/favicons", // Removed trailing slash
  PDF_TICKETS = "whitelabels/{whitelabelId}/events/{eventId}/generated/pdf/tickets", // Removed trailing slash
  EXHIBITOR_TASKS = "whitelabels/{whitelabelId}/events/{eventId}/exhibitor/tasks", // Removed trailing slash
  EXHIBITOR_LOGO = "whitelabels/{whitelabelId}/events/{eventId}/exhibitor/logo", // Removed trailing slash
  EXHIBITOR_VIDEO = "whitelabels/{whitelabelId}/events/{eventId}/exhibitor/{exhibitorId}/video",
  EXHIBITOR_DOCUMENTS = "whitelabels/{whitelabelId}/events/{eventId}/exhibitor/{exhibitorId}/document",
  SESSION_DOCUMENTS = "whitelabels/{whitelabelId}/events/{eventId}/sessions/{sessionId}/document",
  SESSION_VIDEO = "whitelabels/{whitelabelId}/events/{eventId}/sessions/{sessionId}/video",
  ORGANIZER_LOGO = "whitelabels/{whitelabelId}/organizer/{organizerId}/logo", // Removed trailing slash
  ORGANIZER_FAVICON = "whitelabels/{whitelabelId}/organizer/{organizerId}/favicon", // Removed trailing slash
  ORGANIZER_THUMBNAIL = "whitelabels/{whitelabelId}/organizer/{organizerId}/thumbnail", // Removed trailing slash
  FORM_RESULTS = "whitelabels/{whitelabelId}/events/{eventId}/form-results", // Removed trailing slash
  WHATSAPP_TEMPLATES = "whitelabels/{whitelabelId}/events/{eventId}/whatsapp/templates", // Removed trailing slash
  NOTIFICATION_ATTACHMENT = "notification/attachment",
  NOTIFICATION_IMAGES = "notification/images",
}

/**
 * Image subtypes for the IMAGES template
 */
export enum ImageType {
  LOGO = "logos",
  BANNER = "banners",
  FAVICON = "favicons",
  BACKGROUND = "backgrounds",
  GENERAL = "general",
}

/**
 * PDF subtypes for the GENERATED_FILES template with type=pdf
 */
export enum PdfType {
  TICKETS = "tickets",
  INVOICES = "invoices",
  RECEIPTS = "receipts",
  REPORTS = "reports",
}

/**
 * Centralized upload format enum for all upload UIs
 */
export enum UploadFormat {
  VIDEO = "VIDEO",
  IMAGE = "IMAGE",
  DOCUMENT = "DOCUMENT",
}

/**
 * Options for uploading files
 */
export interface UploadOptions {
  contentType?: string;
  accessMode?: FileAccessMode;
  metadata?: Record<string, string>;
  namingStrategy?: FileNamingStrategy;
  directoryTemplate?: DirectoryTemplate;
  templateVars?: Record<string, string | number>;
  customPath?: string;
}

/**
 * Defines maximum file size (in bytes) for different directory templates.
 * Not all templates need to be listed; a default can be applied if a template is not found here.
 */
export const DirectoryTemplateSizeLimits: Partial<
  Record<DirectoryTemplate, number>
> = {
  [DirectoryTemplate.IMAGES]: 5 * 1024 * 1024, // 5MB for general images
  [DirectoryTemplate.BANNERS]: 10 * 1024 * 1024, // 10MB for banners
  [DirectoryTemplate.LOGOS]: 2 * 1024 * 1024, // 2MB for logos
  [DirectoryTemplate.ATTACHMENTS]: 25 * 1024 * 1024, // 25MB for attachments
  [DirectoryTemplate.TEMP]: 10 * 1024 * 1024, // 10MB for temporary files
};

/**
 * Result of an upload operation
 */
export interface UploadResult {
  url: string; // Public URL or relative path
  filePath: string; // Full path within storage
  internalUrl: string; // URL for internal/secure access
  accessMode: FileAccessMode;
  provider: StorageProvider;
}

/**
 * Core interface for all storage adapters
 */
export interface StorageAdapter {
  /**
   * Upload a file to storage
   */
  upload(
    file: Buffer | Blob | Uint8Array,
    filename: string,
    options?: UploadOptions
  ): Promise<UploadResult>;

  /**
   * Generate a presigned URL for secure access
   */
  getPresignedUrl(path: string, expiresIn?: number): Promise<string>;

  /**
   * Get a URL for internal (server-side only) access
   */
  getInternalUrl(path: string): string;

  /**
   * Get the provider type
   */
  getProviderType(): StorageProvider;
}

/**
 * Storage adapter configuration
 */
export interface StorageConfig {
  // Default provider
  defaultProvider: StorageProvider;

  // Provider-specific configurations
  providers: {
    [StorageProvider.GOOGLE_CLOUD]?: {
      bucketName: string;
      keyFilePath?: string;
      projectId: string;
    };

    [StorageProvider.LOCAL]?: {
      basePath: string; // Base directory for file storage
      baseUrl: string; // Base URL for serving files
      signingSecret: string;
    };
  };
}
